import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Modal, Form, Input, Select, message, Space, Tag,
  Drawer, Tooltip, Dropdown
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  ArrowLeftOutlined, FileAddOutlined, DownOutlined, MoreOutlined
} from '@ant-design/icons';
import moment from 'moment';
import EmailTemplateService from '../../../services/EmailTemplateService';
import ROUTES from '../../../constants/routes';
import { PageContainer, PageHeader, StyledTable } from '../../Surveys/styled';
import useResponsive from '../../../hooks/useResponsive';
import HeaderActions from '../../../components/Responsive/HeaderActions';
import MobileCardList from '../../../components/Responsive/MobileCardList';
import EmailBodyEditor from '../Builder/EmailBodyEditor';
import SAMPLE_TEMPLATES from './sampleTemplates';

const { TextArea } = Input;

// Adapter so antd's Form.Item can drive the EmailBodyEditor (which expects
// `value` + `onChange` props — exactly what Form.Item passes down).
const EmailBodyEditorAdapter = ({ value, onChange }) => (
  <EmailBodyEditor value={value || ''} onChange={onChange} />
);

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'survey', label: 'Survey' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'transactional', label: 'Transactional' }
];

const EmailTemplatesPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const result = await EmailTemplateService.listTemplates({ limit: 100 });
    if (result.success) setTemplates(result.data.templates || []);
    else message.error(result.error);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEditor = (template) => {
    setEditing(template);
    if (template) {
      form.setFieldsValue(template);
    } else {
      form.resetFields();
      form.setFieldsValue({ category: 'general' });
    }
    setEditorOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      let result;
      if (editing?._id) {
        result = await EmailTemplateService.updateTemplate(editing._id, values);
      } else {
        result = await EmailTemplateService.createTemplate(values);
      }
      if (result.success) {
        message.success('Template saved');
        setEditorOpen(false);
        load();
      } else {
        message.error(result.error);
      }
    } catch (err) {
      // validation error
    }
  };

  const handleDelete = (template) => {
    Modal.confirm({
      title: `Delete template "${template.name}"?`,
      content: 'The template will be archived. Existing campaigns are not affected.',
      okType: 'danger',
      onOk: async () => {
        const result = await EmailTemplateService.deleteTemplate(template._id);
        if (result.success) {
          message.success('Template deleted');
          load();
        } else {
          message.error(result.error);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, t) => (
        <a onClick={() => openEditor(t)}>
          <strong>{name}</strong>
          {t.description && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t.description}</div>}
        </a>
      )
    },
    {
      title: 'Subject',
      dataIndex: 'subject'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      width: 130,
      render: (c) => <Tag>{c}</Tag>
    },
    {
      title: 'Variables',
      dataIndex: 'variables',
      width: 200,
      render: (vars) => (vars || []).slice(0, 3).map((v) => <Tag key={v}>{`{{${v}}}`}</Tag>)
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      width: 130,
      render: (d) => d ? moment(d).format('MMM D, YYYY') : '—'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, t) => (
        <Space>
          <Tooltip title="Preview">
            <Button type="text" icon={<EyeOutlined />} onClick={() => setPreviewing(t)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditor(t)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(t)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => navigate(ROUTES.ADMIN_EMAIL_CAMPAIGNS)}
            style={{ marginBottom: 8 }}
          >
            Back to Campaigns
          </Button>
        </div>
        <HeaderActions
          size="large"
          primary={{
            label: 'New Template',
            icon: <PlusOutlined />,
            onClick: () => openEditor(null),
            style: { background: '#E31B23', borderColor: '#E31B23' }
          }}
        />
      </PageHeader>

      {isMobile ? (
        <MobileCardList
          records={templates}
          rowKey="_id"
          loading={loading}
          onRowClick={(t) => openEditor(t)}
          emptyText="No templates yet"
          renderCard={(t) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{t.name}</div>
                  {t.description && (
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t.description}</div>
                  )}
                </div>
                {t.category && <Tag style={{ flexShrink: 0 }}>{t.category}</Tag>}
              </div>
              {t.subject && (
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <span style={{ color: '#8c8c8c' }}>Subject: </span>{t.subject}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {t.updatedAt ? `Updated ${moment(t.updatedAt).fromNow()}` : ''}
                </span>
                <Dropdown
                  menu={{
                    items: [
                      { key: 'preview', icon: <EyeOutlined />, label: 'Preview', onClick: () => setPreviewing(t) },
                      { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => openEditor(t) },
                      { type: 'divider' },
                      { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => handleDelete(t) }
                    ]
                  }}
                  trigger={['click']}
                >
                  <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                </Dropdown>
              </div>
            </div>
          )}
        />
      ) : (
        <StyledTable
          columns={columns}
          dataSource={templates}
          rowKey="_id"
          loading={loading}
          size="middle"
        />
      )}

      {/* Editor Modal */}
      <Modal
        title={editing?._id ? 'Edit Template' : 'New Template'}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={handleSave}
        okText="Save"
        width={860}
      >
        {!editing?._id && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 14px',
            marginBottom: 16,
            background: '#fafbfc',
            border: '1px dashed #e5e7eb',
            borderRadius: 8
          }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Not sure where to start? Pick a polished sample to fill in the form.
            </div>
            <Dropdown
              menu={{
                items: SAMPLE_TEMPLATES.map((s) => ({
                  key: s.key,
                  label: (
                    <div style={{ minWidth: 240 }}>
                      <div style={{ fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>{s.description}</div>
                    </div>
                  ),
                  onClick: () => {
                    form.setFieldsValue({
                      name: s.name,
                      description: s.description,
                      category: s.category,
                      subject: s.subject,
                      html: s.html,
                      text: s.text
                    });
                  }
                }))
              }}
            >
              <Button icon={<FileAddOutlined />}>
                Start from sample <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        )}

        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g., Survey Invitation" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Optional internal note" />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Select options={CATEGORIES} />
          </Form.Item>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
            <Input placeholder="Subject line — supports {{variables}}" />
          </Form.Item>
          <Form.Item
            name="html"
            label=""
            rules={[{ required: true, message: 'Email body is required' }]}
          >
            <EmailBodyEditorAdapter />
          </Form.Item>
          <Form.Item name="text" label="Plain Text (optional)">
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Drawer */}
      <Drawer
        title={previewing?.name}
        open={!!previewing}
        onClose={() => setPreviewing(null)}
        width={700}
      >
        {previewing && (
          <div>
            <p><strong>Subject:</strong> {previewing.subject}</p>
            <div
              style={{ border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}
              dangerouslySetInnerHTML={{ __html: previewing.html }}
            />
          </div>
        )}
      </Drawer>
    </PageContainer>
  );
};

export default EmailTemplatesPage;
